import { BILD_FORMATE, type AuftragsTyp, type Sprache, type StartAnfrage } from '@awf/shared';

export interface FormularWerte {
  typ: AuftragsTyp;
  thema: string;
  sprache: Sprache;
  contentTyp: string;
  zusatzinfos: string;
  produkte: string;
  zielgruppe: string;
  ausschlussKeywords: string;
  quellUrl: string;
  verzeichnis: string;
  format: string;
  longtailBevorzugt: boolean;
  sharepointAblage: boolean;
}

export interface Feldfehler {
  feld: keyof FormularWerte;
  text: string;
}

/**
 * Abgelesen aus `pruefeStartAnfrage` in apps/backend/src/runs.ts, nicht geschaetzt. Das
 * Backend schneidet ueber der Grenze STILLSCHWEIGEND ab -- deshalb wird hier beanstandet
 * statt gekuerzt, sonst verschwindet Eingabe ohne Meldung.
 */
export const GRENZEN = {
  thema: 500,
  contentTyp: 50,
  zusatzinfos: 5000,
  produkte: 200,
  zielgruppe: 1000,
  ausschlussKeywords: 500,
  quellUrl: 2000,
  verzeichnis: 200,
} as const;

/** Dieselben Hosts, die `ERLAUBTE_HOSTS` im Backend fuehrt. */
const ERLAUBTE_HOSTS = ['ergo.de'];

export function istErlaubteQuellUrl(roh: string): boolean {
  try {
    const url = new URL(roh);
    if (url.protocol !== 'https:') return false;
    return ERLAUBTE_HOSTS.some((h) => url.hostname === h || url.hostname.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

/** Dieselbe Erkennung wie `istPdpUrl` im Backend (apps/backend/src/runs.ts). */
const PDP_PFAD = /\/(?:Geschaeftskunden-)?Produkte\//i;

export function istPdpUrl(roh: string): boolean {
  return istErlaubteQuellUrl(roh) && PDP_PFAD.test(new URL(roh).pathname);
}

export function leereWerte(typ: AuftragsTyp): FormularWerte {
  return {
    typ,
    thema: '',
    sprache: 'de',
    contentTyp: '',
    zusatzinfos: '',
    produkte: '',
    zielgruppe: '',
    ausschlussKeywords: '',
    quellUrl: '',
    verzeichnis: '',
    format: BILD_FORMATE[0] ?? '',
    longtailBevorzugt: false,
    sharepointAblage: false,
  };
}

const LAENGEN_FELDER = Object.keys(GRENZEN) as (keyof typeof GRENZEN)[];

export function pruefeFormular(werte: FormularWerte): Feldfehler[] {
  const fehler: Feldfehler[] = [];

  // Bei pdp ist das Thema die URL (`themaAus`) -- ein Fehler auf dem unsichtbaren Feld hilft
  // niemandem, die URL-Pruefung unten sagt es am richtigen Feld.
  if (!werte.thema.trim() && werte.typ !== 'pdp') {
    fehler.push({ feld: 'thema', text: 'Bitte geben Sie ein Thema an.' });
  }

  const quellUrl = werte.quellUrl.trim();
  if (quellUrl && !istErlaubteQuellUrl(quellUrl)) {
    fehler.push({ feld: 'quellUrl', text: 'Die Quell-URL muss auf ergo.de liegen.' });
  }

  if (werte.typ === 'analyse' && !werte.verzeichnis.trim() && !quellUrl) {
    fehler.push({
      feld: 'quellUrl',
      text: 'Bitte geben Sie ein Verzeichnis oder eine Ratgeber-URL an.',
    });
  }

  // Wie `pruefeStartAnfrage` im Backend: genau eine Sache je Analyse. Das Formular sperrt das
  // jeweils andere Feld; diese Pruefung faengt den Rest ab (etwa einen vorbelegten Zustand).
  if (werte.typ === 'analyse' && werte.verzeichnis.trim() && quellUrl) {
    fehler.push({
      feld: 'quellUrl',
      text: 'Bitte geben Sie entweder ein Verzeichnis oder eine Ratgeber-URL an, nicht beides.',
    });
  }

  // Dieselbe Regel wie `pruefeStartAnfrage` im Backend: Produktseiten laufen als Typ pdp.
  if (werte.typ === 'analyse' && quellUrl && istErlaubteQuellUrl(quellUrl) && istPdpUrl(quellUrl)) {
    fehler.push({ feld: 'quellUrl', text: 'Für Produktseiten bitte den Typ PDP wählen.' });
  }

  if (werte.typ === 'pdp') {
    if (!quellUrl) {
      fehler.push({ feld: 'quellUrl', text: 'Bitte geben Sie die Adresse der Produktseite an.' });
    } else if (istErlaubteQuellUrl(quellUrl) && !istPdpUrl(quellUrl)) {
      fehler.push({
        feld: 'quellUrl',
        text: 'Die Adresse muss eine Produktseite auf ergo.de sein (…/Produkte/…).',
      });
    }
  }

  // Alle Beanstandungen sammeln, nicht bei der ersten aufhoeren: Ein Formular, das Fehler
  // einzeln nachschiebt, kostet den Nutzer eine Runde pro Fehler.
  for (const feld of LAENGEN_FELDER) {
    const wert = werte[feld].trim();
    if (wert.length > GRENZEN[feld]) {
      fehler.push({
        feld,
        text: `Bitte kürzen Sie die Eingabe auf ${GRENZEN[feld]} Zeichen.`,
      });
    }
  }

  return fehler;
}

export function alsStartAnfrage(werte: FormularWerte): StartAnfrage {
  const anfrage: StartAnfrage = { typ: werte.typ, thema: werte.thema.trim() };

  if (werte.typ === 'ratgeber') anfrage.sprache = werte.sprache;
  if (werte.typ === 'bilder') anfrage.format = werte.format;

  const optionale: (keyof typeof GRENZEN)[] = [
    'contentTyp',
    'zusatzinfos',
    'produkte',
    'zielgruppe',
    'ausschlussKeywords',
    'quellUrl',
    'verzeichnis',
  ];
  for (const feld of optionale) {
    const wert = werte[feld].trim();
    if (wert) anfrage[feld] = wert;
  }

  // Immer mitgeben, auch als false: Das Backend liest sie als `=== true`, ein fehlendes
  // Feld ist dort dasselbe wie false -- aber die Absicht steht so ausdruecklich da.
  anfrage.longtailBevorzugt = werte.longtailBevorzugt;
  anfrage.sharepointAblage = werte.sharepointAblage;

  return anfrage;
}
